import type { FC, PropsWithChildren } from 'react';
import { useRouter } from 'next/router';

import { ArrowMediumDown32, ArrowMediumRight32 } from '@packages/themes/icons';
import {
  ListItemText,
  MenuList,
  MenuItem,
  Typography,
  Box,
  Link,
  ListItemIcon,
  mergeQueryParameters,
} from '@packages/shared';

import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { CategoryItemContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryItemContentFragmentFragmentDoc';
import { CategoryItemTextFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryItemTextFragmentFragmentDoc';
import { CategoryMenuItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryMenuItemFragmentFragmentDoc';
import { ProductCategoryNavigationDesktopFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductCategoryNavigationDesktopFragmentFragmentDoc';
import { extractDisplayMode, extractOrder, extractSelectedFilterValues } from '../../queryEncoding';

/* GraphQL */ `
  fragment CategoryItemContentFragment on FlatCategoryItem {
    level
    pathName
    hasChildren
    isSelected
  }
`;

/** Displays Content of nested menu item.
 *  NOTE: Level starts with 2 for Category navigation.
 *  Level 2 should display the arrow icon, if it has children.
 *  - Arrow down when children are expanded
 *  - Arrow right when children are collapsed
 *  Level 3 and above should display a plus or minus sign, if it has children.
 *  - Plus sign when children are collapsed
 *  - Minus sign when children are expanded
 *  If there are no children no icon should be displayed */
const CategoryItemContent = ({
  maskedCategory,
  children,
}: PropsWithChildren<{
  maskedCategory: FragmentType<typeof CategoryItemContentFragmentFragmentDoc>;
}>) => {
  const category = getFragmentData(CategoryItemContentFragmentFragmentDoc, maskedCategory);

  return category.level === 2 ? (
    <>
      {children}
      {category.hasChildren && (
        <ListItemIcon>
          {category.isSelected ? <ArrowMediumDown32 /> : <ArrowMediumRight32 />}
        </ListItemIcon>
      )}
    </>
  ) : (
    <>
      {category.hasChildren ? (
        <Typography variant="h5" component="span" sx={{ marginRight: 1, marginLeft: 0.5 }}>
          {category.isSelected ? '-' : '+'}
        </Typography>
      ) : (
        <Box sx={{ marginRight: 2.3 }} />
      )}
      {children}
    </>
  );
};

/* GraphQL */ `
  fragment CategoryItemTextFragment on FlatCategoryItem {
    name
    isSelected
  }
`;

/** Displays the name of the category */
const CategoryItemText = ({
  maskedCategory,
}: {
  maskedCategory: FragmentType<typeof CategoryItemTextFragmentFragmentDoc>;
}) => {
  const category = getFragmentData(CategoryItemTextFragmentFragmentDoc, maskedCategory);

  return (
    <ListItemText
      primaryTypographyProps={{
        noWrap: true,
        title: category.name,
        sx: { fontWeight: category.isSelected ? 'bold' : 'inherit' },
      }}
    >
      {category.name}
    </ListItemText>
  );
};

/* GraphQL */ `
  fragment CategoryMenuItemFragment on FlatCategoryItem {
    level
    pathName
    ...CategoryItemContentFragment
    ...CategoryItemTextFragment
  }
`;

type CategoryMenuItemProps = {
  maskedCategory: FragmentType<typeof CategoryMenuItemFragmentFragmentDoc>;
  isChildOfActiveCategory: boolean;
};

/** Displays MenuItem for every nested child through recursion */
const CategoryMenuItem: FC<CategoryMenuItemProps> = ({
  maskedCategory,
  isChildOfActiveCategory,
}) => {
  const { query } = useRouter();
  const category = getFragmentData(CategoryMenuItemFragmentFragmentDoc, maskedCategory);

  const href = isChildOfActiveCategory
    ? mergeQueryParameters(category.pathName, {
        ...extractDisplayMode(query),
        ...extractOrder(query),
        ...extractSelectedFilterValues(query),
      })
    : category.pathName;

  return (
    <MenuItem
      dense={category.level > 2}
      sx={{
        paddingLeft: category.level > 3 ? category.level - 2 : 1,
      }}
      component={Link}
      href={href}
    >
      <CategoryItemContent maskedCategory={category}>
        <CategoryItemText maskedCategory={category} />
      </CategoryItemContent>
    </MenuItem>
  );
};

/* GraphQL */ `
  fragment ProductCategoryNavigationDesktopFragment on FlatCategoryItem {
    id
    isSelectedExact
    # This property is not used in this component, but only as a workaround
    # since CategoryPageDefaultContent requires it as well as CategoryFilterNavigationMobileFragment
    # which is also of type FlatCategoryItem but needs to have an alias due to a union type difference
    # and needs also the same data structure because of a weird bug in the fragment matcher which returns "null" in this case
    isSelectedIndirectly
    parentId
    level
    ...CategoryMenuItemFragment
  }
`;

export type ProductCategoryNavigationDesktopProps = {
  maskedItems: FragmentType<typeof ProductCategoryNavigationDesktopFragmentFragmentDoc>[];
};

/**
 * Displays the category navigation in desktop view
 * */
export const ProductCategoryNavigationDesktop: FC<ProductCategoryNavigationDesktopProps> = ({
  maskedItems,
}) => {
  const items = getFragmentData(
    ProductCategoryNavigationDesktopFragmentFragmentDoc,
    maskedItems,
  ).filter((item) => item.level >= 2); // forward compatibility for future changes, where `root` (really the L1 category) will be included in the categories itself and not provided separately

  const activeCategory = items.find((item) => item.isSelectedExact);

  return (
    <MenuList data-testid="product-category-navigation-desktop" sx={{ paddingBottom: 0 }}>
      {items.map((item) => (
        <CategoryMenuItem
          key={item.id}
          maskedCategory={item}
          isChildOfActiveCategory={activeCategory?.id === item.parentId}
        />
      ))}
    </MenuList>
  );
};
